const PREFERRED_DISPLAY_NAMES = {
  aaronha01: "Hank Aaron",
  ruthba01: "Babe Ruth",
  willite01: "Ted Williams",
  pujolal01: "Albert Pujols",
};

const DIMINUTIVES = {
  henry: ["hank"],
  theodore: ["ted", "teddy"],
  joseph: ["joe", "joey"],
  robert: ["bob", "bobby", "rob"],
  william: ["bill", "billy", "will", "willy", "willie"],
  james: ["jim", "jimmy"],
  john: ["jack", "johnny"],
  anthony: ["tony"],
  francis: ["frank"],
  richard: ["dick", "rich", "rick"],
  edward: ["ed", "eddie"],
  alberto: ["albert", "al"],
  albert: ["al"],
  donald: ["don", "donnie"],
  michael: ["mike", "mick"],
  charles: ["chuck", "charlie"],
  tyrus: ["ty"],
  alexander: ["alex"],
};

const NAME_SUFFIXES = new Set(["jr", "jr.", "sr", "sr.", "ii", "iii", "iv", "v"]);
const SKIP_ALIAS_PREFIX = /^(the|la|el|los|las)\s/i;
const NAME_TOKEN = /^[\p{L}']+$/u;
const KNOWN_NAME_CHAR_REPAIRS = [
  [/Jos\uFFFD/g, "José"],
  [/Mart\uFFFDnez/gi, "Martínez"],
  [/Garc\uFFFDa/gi, "García"],
  [/Garc\uFFFD/gi, "García"],
  [/P\uFFFDrez/gi, "Pérez"],
  [/Gonz\uFFFDlez/gi, "González"],
  [/S\uFFFDnchez/gi, "Sánchez"],
  [/Chac\uFFFDn/gi, "Chacón"],
  [/Hern\uFFFDndez/gi, "Hernández"],
  [/Hern\uFFFDn/gi, "Hernán"],
  [/Rh\uFFFDal/gi, "Rhéal"],
  [/Pe\uFFFDa/gi, "Peña"],
  [/Liv\uFFFDn/gi, "Liván"],
  [/\uFFFDrias/g, "Árias"],
  [/Garcíaa/g, "García"],
  [/Peñaa/g, "Peña"],
];

export const restoreKnownNameChars = (name) =>
  KNOWN_NAME_CHAR_REPAIRS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    String(name || "")
  );

const foldName = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase();

const parseLegalName = (legalName) => {
  const parts = String(legalName || "")
    .replace(/,/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return { first: "", middles: [], last: "" };
  }

  const lastPart = parts[parts.length - 1];
  const withoutSuffix = NAME_SUFFIXES.has(foldName(lastPart))
    ? parts.slice(0, -1)
    : parts;

  if (withoutSuffix.length === 0) {
    return { first: "", middles: [], last: lastPart };
  }

  return {
    first: withoutSuffix[0],
    middles: withoutSuffix.slice(1, -1),
    last: withoutSuffix[withoutSuffix.length - 1],
  };
};

const isCleanShortNick = (nickName) => {
  const nick = String(nickName || "").trim();
  if (!nick || foldName(nick) === "none") return false;
  if (/[()]/.test(nick) || /\bor\b/i.test(nick) || nick.includes(",")) {
    return false;
  }
  const words = nick.split(/\s+/);
  return nick.length <= 20 && words.length <= 2;
};

const parseAliases = (nickName) => {
  const raw = String(nickName || "").trim();
  if (!raw || foldName(raw) === "none") return [];
  return raw
    .split(/\s+or\s+|,\s*/i)
    .map((alias) => alias.trim())
    .filter(Boolean);
};

const extractGivenToken = (alias) => {
  const withoutParens = String(alias || "")
    .replace(/\s*\([^)]*\)/g, "")
    .trim();
  if (!withoutParens || SKIP_ALIAS_PREFIX.test(withoutParens)) return null;
  const words = withoutParens.split(/\s+/);
  if (words.length > 3) return null;
  const token = words[words.length - 1].replace(/[.,]/g, "");
  if (!NAME_TOKEN.test(token) || token.length > 20) return null;
  return token;
};

const isDiminutiveOf = (token, givenName) => {
  const nicks = DIMINUTIVES[foldName(givenName)] || [];
  return nicks.includes(foldName(token));
};

const matchesMiddleName = (token, middleName) => {
  const folded = foldName(token);
  const middle = foldName(middleName);
  if (!folded || !middle) return false;
  if (folded === middle) return true;
  return (
    folded.length >= 4 &&
    middle.length >= 4 &&
    (middle.startsWith(folded) || folded.startsWith(middle))
  );
};

const pickAliasGivenName = (nickName, { first, middles }) => {
  const givenNames = [first, ...middles].filter(Boolean);
  const tokens = parseAliases(nickName).map(extractGivenToken).filter(Boolean);

  const diminutive = tokens.find((token) =>
    givenNames.some((given) => isDiminutiveOf(token, given))
  );
  if (diminutive) return diminutive;

  return tokens.find((token) =>
    middles.some((middle) => matchesMiddleName(token, middle))
  );
};

const displayFromCleanNick = (nickName, lastName) => {
  const words = String(nickName || "")
    .trim()
    .split(/\s+/);
  if (!lastName || words.length === 0) return null;
  if (words.length === 1) return `${words[0]} ${lastName}`;
  if (foldName(words[1]) === foldName(lastName)) return words.join(" ");
  return `${words[words.length - 1]} ${lastName}`;
};

const commonNameFromLegal = ({ first, last }) => {
  if (!first) return last || "";
  if (!last || foldName(first) === foldName(last)) return first;
  return `${first} ${last}`;
};

export const getPlayerScoreboardName = ({
  playerId,
  birthName,
  nickName,
  name,
} = {}) => {
  if (playerId && PREFERRED_DISPLAY_NAMES[playerId]) {
    return PREFERRED_DISPLAY_NAMES[playerId];
  }

  const legal = restoreKnownNameChars(birthName || name || "").trim();
  if (!legal) return "";

  const parsed = parseLegalName(legal);
  const aliasGiven = pickAliasGivenName(nickName, parsed);
  if (aliasGiven && parsed.last) {
    return `${aliasGiven} ${parsed.last}`;
  }

  if (isCleanShortNick(nickName)) {
    const fromNick = displayFromCleanNick(nickName, parsed.last);
    if (fromNick) return fromNick;
  }

  return commonNameFromLegal(parsed) || legal;
};

const firstNonEmpty = (...values) => {
  for (const value of values) {
    const trimmed = String(value || "").trim();
    if (trimmed) return trimmed;
  }
  return "";
};

export const scoreboardNameFromPlayer = (
  player,
  fallback = "Unknown Player"
) => {
  if (!player) return fallback;
  if (typeof player === "string") {
    return getPlayerScoreboardName({ name: player }) || fallback;
  }
  const bio = player.bio || {};
  const bioData = player.bioData || {};
  return (
    getPlayerScoreboardName({
      playerId: player.playerId || player.id,
      birthName: firstNonEmpty(
        player.birthName,
        bio.birthName,
        bioData.birthName
      ),
      nickName: firstNonEmpty(
        player.nickName,
        bio.nickName,
        bioData.nickName
      ),
      name: firstNonEmpty(
        player.name,
        player.playerName,
        player.displayName,
        bio.displayName,
        bioData.displayName,
        player.fullName,
        bio.fullName,
        bioData.fullName
      ),
    }) || fallback
  );
};
